import React, {useEffect, useMemo, useState} from "react";
import Api from "../../../../../../core/services/api/api";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {themeEditorApis} from "../../../../../../core/models/static/api-endpoints/api_endpoints";
import {Fade, Paper} from "@mui/material";
import {Row} from "reactstrap";
import {ReactComponent as Svg} from '../../../../../../assets/images/art-like/web-content-management.svg';
import Button from "../../../../../components/base/button/button";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import {usePartnerInformation} from "../../../../../hooks/use-partner-information";
import ToastService from "../../../../../../core/services/toast";

const UserPlatformContentManagementView = () => {
    const [token, setToken] = useState(undefined);
    const [generatingToken, setGeneratingToken] = useState(true);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('user-platform-content-managements-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const partnerInfo = usePartnerInformation();


    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.userPlatformContentManagement);
        configureBehavioralTool((tags) => tags.userPlatformContentManagement);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**@type {boolean}*/
    const loading = useMemo(() => generatingToken, [generatingToken])

    /**
     * As soon as the component mounts:
     * - fetches the information of the current partner from the server
     * - fetches the edit mode's token of the theme editor from the server.
     */
    useEffect(() => {
        generateEditModeToken().then();
    }, [])

    /**
     * Fetches the edit mode's token of the theme editor from the server.
     * @return {Promise<void>}
     */
    const generateEditModeToken = async () => {
        setGeneratingToken(true);
        const response = await Api.getThemeEditorEditToken();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setToken(response?.data?.token);
        }
        setGeneratingToken(false);
    }

    /**
     * Navigates the user to the theme editor view of the client website.
     */
    const navigateToWebThemeEditor = () => {
        const link = partnerInfo?.website?.concat(`/dynamic-content-editor?validationToken=${token}&baseUrl=${themeEditorApis.baseUrl}`);
        if (link) {
            window.location = partnerInfo?.website?.concat(`/dynamic-content-editor?validationToken=${token}&baseUrl=${themeEditorApis.baseUrl}`);
        } else {
            ToastService.warning({
                title: "Client website URL not found",
                description: "We couldn't find the URL of your client's website. Please contact support to resolve this issue."
            })
        }
    }

    return (
        <>
            <Paper className='paper flex-wrap background-white p-3 py-4 sales-person-dashboard'>
                <Row className={'w-100 mb-5 mt-2 d-flex justify-content-center'}>
                    <Svg/>
                </Row>
                <Row className={'w-100 mb-4 d-flex justify-content-center'}>
                    <p className={'text-sm text-color-1 font-weight-semi-bold'}>
                        You can edit your clients' website content by clicking on the button below.
                    </p>
                </Row>
                <Row className={'w-100 mb-5 d-flex flex-column align-items-center'}>
                    {
                        <Fade in={!loading}>
                            <Button
                                variant={'outlined'}
                                color={'primary'}
                                onClick={() => navigateToWebThemeEditor()}
                            >
                                Edit client's website content.
                            </Button>
                        </Fade>
                    }
                </Row>
            </Paper>
        </>
    )
}

export default UserPlatformContentManagementView;
